import { create } from 'zustand';
import type {
  AgentSessionDTO,
  AgentSessionStatus,
  ApprovalGateData,
} from '@/shared/types/agentSession';

const TERMINAL_STATUSES: readonly AgentSessionStatus[] = [
  'done',
  'failed',
  'cancelled',
] as const;

export interface AgentSessionStore {
  sessions: Record<string, AgentSessionDTO>;
  pendingApprovals: Record<string, ApprovalGateData>;
  isBudgetExceeded: boolean;

  // Actions
  setSession: (session: AgentSessionDTO) => void;
  updateSessionStatus: (
    id: string,
    status: AgentSessionStatus,
    extra?: Partial<AgentSessionDTO>
  ) => void;
  removeSession: (id: string) => void;
  addPendingApproval: (approval: ApprovalGateData) => void;
  removePendingApproval: (approvalId: string) => void;
  setBudgetExceeded: (exceeded: boolean) => void;

  // Selectors
  getActiveSessions: (chatSessionId: string) => AgentSessionDTO[];
  getSession: (id: string) => AgentSessionDTO | undefined;
}

export const useAgentSessionStore = create<AgentSessionStore>((set, get) => ({
  sessions: {},
  pendingApprovals: {},
  isBudgetExceeded: false,

  setSession: (session: AgentSessionDTO) => {
    set((state) => ({
      sessions: {
        ...state.sessions,
        [session.id]: session,
      },
    }));
  },

  updateSessionStatus: (
    id: string,
    status: AgentSessionStatus,
    extra?: Partial<AgentSessionDTO>
  ) => {
    set((state) => {
      const existing = state.sessions[id];
      if (!existing) {
        // If session not yet tracked, create placeholder if extra has required info
        if (extra && extra.chat_session_id) {
          const newSession: AgentSessionDTO = {
            id,
            chat_session_id: extra.chat_session_id,
            user_id: extra.user_id ?? '',
            workspace_id: extra.workspace_id ?? '',
            persona_id: extra.persona_id ?? '',
            persona_name: extra.persona_name ?? null,
            intent: extra.intent ?? 'simple',
            lifecycle_mode: extra.lifecycle_mode ?? 'ephemeral',
            status,
            parent_session_id: extra.parent_session_id ?? null,
            kwp_agent_id: extra.kwp_agent_id ?? null,
            kwp_node_id: extra.kwp_node_id ?? null,
            token_budget: extra.token_budget ?? 100_000,
            tokens_consumed: extra.tokens_consumed ?? 0,
            started_at: extra.started_at ?? new Date().toISOString(),
            completed_at: TERMINAL_STATUSES.includes(status) ? new Date().toISOString() : null,
            last_active_at: new Date().toISOString(),
            error_code: extra.error_code ?? null,
            error_message: extra.error_message ?? null,
            created_at: extra.created_at ?? new Date().toISOString(),
            updated_at: new Date().toISOString(),
            ...extra,
          };
          return {
            sessions: {
              ...state.sessions,
              [id]: newSession,
            },
          };
        }
        return state;
      }

      const updated: AgentSessionDTO = {
        ...existing,
        ...extra,
        status,
        last_active_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        completed_at: TERMINAL_STATUSES.includes(status)
          ? (extra?.completed_at ?? new Date().toISOString())
          : existing.completed_at,
      };

      return {
        sessions: {
          ...state.sessions,
          [id]: updated,
        },
      };
    });
  },

  removeSession: (id: string) => {
    set((state) => {
      const { [id]: _, ...rest } = state.sessions;
      return { sessions: rest };
    });
  },

  addPendingApproval: (approval: ApprovalGateData) => {
    set((state) => ({
      pendingApprovals: {
        ...state.pendingApprovals,
        [approval.approval_id]: approval,
      },
    }));
  },

  removePendingApproval: (approvalId: string) => {
    set((state) => {
      const { [approvalId]: _, ...rest } = state.pendingApprovals;
      return { pendingApprovals: rest };
    });
  },

  setBudgetExceeded: (exceeded: boolean) => {
    set({ isBudgetExceeded: exceeded });
  },

  getActiveSessions: (chatSessionId: string) => {
    const { sessions } = get();
    return Object.values(sessions).filter(
      (s) =>
        s.chat_session_id === chatSessionId &&
        !TERMINAL_STATUSES.includes(s.status)
    );
  },

  getSession: (id: string) => {
    return get().sessions[id];
  },
}));
